/**
 * The licence register's three surfaces, mounted through the `routes.extra.ts`
 * seam exactly as the five standalone registers are.
 *
 * =============================================================================
 * THE PATH IS THE NAV REGISTRY'S, AND THE NAV ROW IS NEW RATHER THAN RESERVED
 * =============================================================================
 * `licences` is copied from the `Licences` row this change adds to
 * `domain/nav.ts`. That row is not a reservation being flipped: the registry has
 * held `reports/received`, `reports/complete`, `billing` and `parties` as
 * `available: false` rows since Phase 3, and not one of them is a licence
 * register. `nav.test.ts` moves with the row, and its docblock records that the
 * pin was EXTENDED rather than loosened.
 *
 * =============================================================================
 * `by-serial/:serial` MATCHES THE API's OWN SEGMENT, DELIBERATELY
 * =============================================================================
 * `GET /countries/:cc/tally-licences/by-serial/:serial` is the route behind this
 * screen, and naming the screen's URL after the resource means anybody reading a
 * server log can find the screen that produced it — the rule
 * `features/renewals/routes.tsx` records for `renewal-drafts`.
 *
 * It is also NOT `licences/:id`. A Tally serial is a string that is unique per
 * country, not a row id; the API's own route binds no entity for that reason, and
 * a URL shaped like an id lookup would invite the next person to add one.
 *
 * =============================================================================
 * `parties/:partyId/licences` — A TAB WHOSE PAGE HAS SINCE BEEN BUILT
 * =============================================================================
 * This header used to record that the party record page did not exist, so
 * `PartyLicencesRoute` mounted the panel at the URL the tab would own when it
 * did. That page landed with feat/party-referral-screen (`PartyRecordPage`,
 * `parties/:partyId`) and mounts `PartyLicenceTab` directly — the one-line move
 * this arrangement existed to keep cheap. This route STAYS for deep links and
 * for the `?name=` entry; nothing had to move, exactly as predicted.
 *
 * NO PROVIDER LAYOUT. The quote routes wrap themselves in a `ToastProvider`
 * because their action navigates and the message must outlive the move. Nothing
 * on any of these three screens mutates anything — there is no write route on the
 * whole licence API — so a provider here would exist to carry a toast that nothing
 * can raise.
 *
 * All three sit INSIDE `RequireCountry` + `AppShell` like every other country
 * route, so they can rely on `useCountryCode()` and on a resolved session.
 */

import type { RouteObject } from 'react-router-dom';

import { DisputeReviewPage } from './DisputeReviewPage';
import { LicenceRegisterPage } from './LicenceRegisterPage';
import { SerialLookupPage } from './SerialLookupPage';
import { PartyLicencesRoute } from './PartyLicenceTab';

export const tallyLicenceRoutes: RouteObject[] = [
  { path: 'licences', element: <LicenceRegisterPage /> },
  /*
   * ⚠ THE REVIEW SURFACE IS A CHILD OF THE REGISTER, NOT A SECOND NAV ROW.
   *
   * The previous cluster put `Licences` under Renewals and flagged that a SECOND
   * licence destination would be the moment the register earns its own sidebar
   * section — a fifth registry in `domain/nav.ts` plus a line in
   * `components/shell/Sidebar.tsx`, both files this cluster does not own. That
   * cost is REPORTED rather than folded in: the review screen is reached from the
   * register it reviews, which is also where its reader already is. `licences/…`
   * keeps it under the destination the nav row already names, so adopting the
   * fifth section later moves nothing.
   *
   * `disputes` is a literal segment and cannot collide with `by-serial/:serial`.
   */
  { path: 'licences/disputes', element: <DisputeReviewPage /> },
  { path: 'licences/by-serial/:serial', element: <SerialLookupPage /> },
  { path: 'parties/:partyId/licences', element: <PartyLicencesRoute /> },
];
